import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

const Notification = ({ notification, onClose }) => {
    const [isExiting, setIsExiting] = useState(false);
    const [progress, setProgress] = useState(100);

    // AJOUT : Récupérer pausedVideo
    const { id, message, type, duration, pausedVideo } = notification;

    // Configuration des styles selon le type
    const config = {
        success: {
            icon: CheckCircle,
            gradient: 'from-green-600 to-emerald-600',
            border: 'border-green-500/30',
            bg: 'from-green-600/20 to-emerald-600/20',
            iconColor: 'text-green-400',
            progressBar: 'bg-green-500'
        },
        error: {
            icon: AlertCircle,
            gradient: 'from-red-600 to-rose-600',
            border: 'border-red-500/30',
            bg: 'from-red-600/20 to-rose-600/20',
            iconColor: 'text-red-400',
            progressBar: 'bg-red-500'
        },
        warning: {
            icon: AlertTriangle,
            gradient: 'from-yellow-600 to-orange-600',
            border: 'border-yellow-500/30',
            bg: 'from-yellow-600/20 to-orange-600/20',
            iconColor: 'text-yellow-400',
            progressBar: 'bg-yellow-500'
        },
        info: {
            icon: Info,
            gradient: 'from-blue-600 to-cyan-600',
            border: 'border-blue-500/30',
            bg: 'from-blue-600/20 to-cyan-600/20',
            iconColor: 'text-blue-400',
            progressBar: 'bg-blue-500'
        }
    };

    const style = config[type] || config.info;
    const Icon = style.icon;

    // Gestion de la barre de progression
    useEffect(() => {
        if (duration <= 0) return;

        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
            setProgress(remaining);

            if (remaining === 0) {
                clearInterval(interval);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [duration]);

    // Gestion de la fermeture avec animation
    const handleClose = () => {
        setIsExiting(true);
        setTimeout(() => {
            onClose(id);
        }, 300);
    };

    return (
        <div
            className={`transform transition-all duration-300 ${
                isExiting
                    ? 'translate-x-full opacity-0'
                    : 'translate-x-0 opacity-100'
            }`}
        >
            <div
                className={`relative bg-gradient-to-r ${style.bg} backdrop-blur-xl rounded-xl border ${style.border} shadow-2xl overflow-hidden w-full sm:min-w-[320px] max-w-md ${
                    type === 'warning' ? 'ring-2 ring-yellow-500/50 animate-pulse' : ''
                }`}
            >
                {/* Barre de progression */}
                {duration > 0 && (
                    <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
                        <div
                            className={`h-full ${style.progressBar} transition-all duration-100 ease-linear`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )}

                <div className="flex items-start gap-3 p-4 pt-5">
                    {/* Icône */}
                    <div className={`flex-shrink-0 p-2 rounded-full bg-gradient-to-r ${style.gradient}`}>
                        <Icon className="w-5 h-5 text-white" />
                    </div>

                    {/* Message */}
                    <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium leading-relaxed">
                            {message}
                        </p>

                        {/* AJOUT : Badge pour les warnings avec pause vidéo */}
                        {type === 'warning' && (
                            <div className="mt-2 flex items-center gap-2">
                                <span className="bg-yellow-500/30 text-yellow-200 text-xs px-2 py-1 rounded-full font-bold">
                                    ⚠️ ATTENTION
                                </span>
                                {pausedVideo && (
                                    <span className="text-white/70 text-xs">
                                        (Vidéo en pause)
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Bouton fermer */}
                    <button
                        onClick={handleClose}
                        className="flex-shrink-0 text-white/60 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg"
                        aria-label="Fermer la notification"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Notification;